import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';

const HOST_API = process.env.REACT_APP_URL;

const fmtFecha = (fechaStr) => {
    if (!fechaStr) return '';
    return new Date(fechaStr).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtFechaHora = (fechaStr) => {
    if (!fechaStr) return '';
    return new Date(fechaStr).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const ESTADOS = ['Pendiente', 'En proceso', 'Solucionado', 'No solucionado'];

const ESTADO_OPTIONS = [
    { label: 'Todos', value: '' },
    ...ESTADOS.map(e => ({ label: e, value: e })),
];

const ESTADO_COLORS = {
    'Pendiente':       { bg: '#fffaeb', color: '#b54708', border: '#fedf89' },
    'En proceso':      { bg: '#eff8ff', color: '#1570ef', border: '#b2ddff' },
    'Solucionado':     { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
    'No solucionado':  { bg: '#fef3f2', color: '#f04438', border: '#fecdca' },
};

const estadoBadge = (estado) => {
    const s = ESTADO_COLORS[estado] || { bg: '#f2f4f7', color: '#667085', border: '#d0d5dd' };
    return (
        <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99 }}>
            {estado}
        </span>
    );
};

const ReclamosAdmin = () => {
    const toast      = useRef(null);
    const navigate   = useNavigate();
    const { barrio } = useContext(BarrioContext);
    const { administracionId, usuarioId } = useAuth();

    const [reclamos,     setReclamos]     = useState([]);
    const [tipos,        setTipos]        = useState([]);
    const [loading,      setLoading]      = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');
    const [filtroTipo,   setFiltroTipo]   = useState(null);
    const [filtroEstado, setFiltroEstado] = useState('');

    // ─── Modal de seguimiento / trazabilidad ──────────────────────────────────────
    const [modalVisible,        setModalVisible]        = useState(false);
    const [reclamoActivo,       setReclamoActivo]        = useState(null);
    const [seguimientos,        setSeguimientos]         = useState([]);
    const [loadingSeguimientos, setLoadingSeguimientos]  = useState(false);
    const [nuevoEstado,         setNuevoEstado]          = useState(null);
    const [nuevoTexto,          setNuevoTexto]           = useState('');
    const [guardando,           setGuardando]            = useState(false);

    const fetchReclamos = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${HOST_API}api/reclamos/GetReclamos?administracionId=${administracionId}&barrioId=${barrio.id}`
            );
            setReclamos(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los reclamos', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, administracionId]);

    const fetchTipos = useCallback(async () => {
        try {
            const { data } = await axios.get(`${HOST_API}api/tiposReclamo/GetTiposReclamo?administracionId=${administracionId}`);
            setTipos([{ tipoReclamoId: null, nombre: 'Todos' }, ...data]);
        } catch { /* silencioso */ }
    }, [administracionId]);

    useEffect(() => { fetchReclamos(); fetchTipos(); }, [fetchReclamos, fetchTipos]);

    const handleEliminar = (r) => {
        confirmDialog({
            message: `¿Eliminar el reclamo de ${r.solicitante} (${r.tipoReclamoNombre})?`,
            header: 'Eliminar reclamo',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sí, eliminar',
            rejectLabel: 'No',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/reclamos/DeleteReclamo?reclamoId=${r.reclamoId}&administracionId=${administracionId}`);
                    fetchReclamos();
                    toast.current.show({ severity: 'info', summary: 'Eliminado', detail: 'Reclamo eliminado', life: 2500 });
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el reclamo', life: 3000 });
                }
            },
        });
    };

    // ─── Seguimiento ──────────────────────────────────────────────────────────────
    const fetchSeguimientos = useCallback(async (reclamoId) => {
        setLoadingSeguimientos(true);
        try {
            const { data } = await axios.get(`${HOST_API}api/reclamos/GetSeguimientos`, {
                params: { reclamoId, administracionId },
            });
            setSeguimientos(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar el seguimiento', life: 3000 });
        } finally {
            setLoadingSeguimientos(false);
        }
    }, [administracionId]);

    const abrirSeguimiento = (r) => {
        setReclamoActivo(r);
        setNuevoEstado(r.estado);
        setNuevoTexto('');
        setModalVisible(true);
        fetchSeguimientos(r.reclamoId);
    };

    const guardarSeguimiento = async () => {
        if (!nuevoEstado) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione el estado', life: 3000 }); return; }

        setGuardando(true);
        try {
            await axios.post(`${HOST_API}api/reclamos/AgregarSeguimiento`, {
                reclamoId: reclamoActivo.reclamoId,
                administracionId,
                estado: nuevoEstado,
                texto: nuevoTexto || null,
                usuarioId,
            });
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Seguimiento agregado', life: 2500 });
            setNuevoTexto('');
            await fetchSeguimientos(reclamoActivo.reclamoId);
            setReclamoActivo(prev => ({ ...prev, estado: nuevoEstado }));
            fetchReclamos();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'No se pudo agregar el seguimiento', life: 4000 });
        } finally {
            setGuardando(false);
        }
    };

    const reclamosFiltrados = reclamos.filter(r => {
        if (filtroTipo   && r.tipoReclamoId !== filtroTipo) return false;
        if (filtroEstado && r.estado !== filtroEstado) return false;
        if (globalFilter) {
            const q = globalFilter.toLowerCase();
            return (
                r.solicitante?.toLowerCase().includes(q) ||
                r.tipoReclamoNombre?.toLowerCase().includes(q) ||
                r.descripcion?.toLowerCase().includes(q)
            );
        }
        return true;
    });

    const pendientesCount = reclamos.filter(r => r.estado === 'Pendiente').length;

    // ── Templates ──
    const estadoTemplate     = (r) => estadoBadge(r.estado);
    const fechaTemplate      = (r) => fmtFecha(r.fecha);
    const descripcionTemplate = (r) => r.descripcion
        ? <span title={r.descripcion} style={{ color: '#344054' }}>
            {r.descripcion.length > 70 ? r.descripcion.substring(0, 70) + '…' : r.descripcion}
          </span>
        : <span style={{ color: '#d0d5dd' }}>—</span>;

    const accionesTemplate = (r) => (
        <div style={{ display: 'flex', gap: '0.25rem' }}>
            <Button
                icon="pi pi-comments"
                rounded text
                tooltip="Ver seguimiento"
                tooltipOptions={{ position: 'left' }}
                onClick={() => abrirSeguimiento(r)}
            />
            <Button
                icon="pi pi-trash"
                rounded text severity="danger"
                tooltip="Eliminar"
                tooltipOptions={{ position: 'left' }}
                onClick={() => handleEliminar(r)}
            />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

<LoadingOverlay visible={loading} />

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div>
                        <h4 style={{ margin: 0, color: '#101828' }}>Reclamos</h4>
                        <p style={{ margin: '0.3rem 0 0', color: '#667085', fontSize: '0.88rem' }}>Gestión de reclamos del barrio</p>
                    </div>
                    {pendientesCount > 0 && (
                        <span style={{ background: '#fef3f2', color: '#f04438', border: '1px solid #fecdca', fontWeight: 700, fontSize: '0.8rem', padding: '0.2rem 0.65rem', borderRadius: 99 }}>
                            {pendientesCount} pendiente{pendientesCount !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>
            </div>

            {!barrio?.id
                ? <p style={{ color: '#98a2b3' }}>Seleccioná un barrio para ver los reclamos.</p>
                : (
                    <>
                        {/* Filtros */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center' }}>
                                <Dropdown
                                    value={filtroTipo}
                                    options={tipos}
                                    optionLabel="nombre"
                                    optionValue="tipoReclamoId"
                                    onChange={(e) => setFiltroTipo(e.value)}
                                    placeholder="Tipo"
                                    className="p-inputtext-sm"
                                    style={{ minWidth: 180 }}
                                />
                                <Dropdown
                                    value={filtroEstado}
                                    options={ESTADO_OPTIONS}
                                    optionLabel="label"
                                    optionValue="value"
                                    onChange={(e) => setFiltroEstado(e.value)}
                                    className="p-inputtext-sm"
                                    style={{ minWidth: 160 }}
                                />
                                {(filtroTipo || filtroEstado) && (
                                    <Button icon="pi pi-filter-slash" text severity="secondary" tooltip="Limpiar filtros"
                                        onClick={() => { setFiltroTipo(null); setFiltroEstado(''); }} />
                                )}
                            </div>
                            <span className="p-input-icon-left">
                                <i className="pi pi-search" style={{ left: '0.75rem' }} />
                                <InputText
                                    value={globalFilter}
                                    onChange={(e) => setGlobalFilter(e.target.value)}
                                    placeholder="Buscar…"
                                    className="p-inputtext-sm"
                                    style={{ paddingLeft: '2rem', width: 200 }}
                                />
                            </span>
                        </div>

                        <DataTable
                            value={reclamosFiltrados}
                            paginator
                            rows={15}
                            rowsPerPageOptions={[10, 15, 30, 50]}
                            emptyMessage="No hay reclamos para mostrar"
                            sortField="fecha"
                            sortOrder={-1}
                            rowClassName={(r) => (r.estado === 'Solucionado' || r.estado === 'No solucionado') ? 'opacity-50' : ''}
                            style={{ fontSize: '0.88rem' }}
                        >
                            <Column field="tipoReclamoNombre" header="Tipo"        sortable style={{ minWidth: 150 }} />
                            <Column field="solicitante"       header="Solicitante" sortable style={{ minWidth: 150 }} />
                            <Column field="fecha"             header="Fecha"       sortable body={fechaTemplate}       style={{ minWidth: 100 }} />
                            <Column header="Descripción"      body={descripcionTemplate} style={{ minWidth: 200 }} />
                            <Column header="Foto" body={(r) => r.fotoUrl
                                ? <a href={`${HOST_API}${r.fotoUrl}`.replace(/([^:])\/\/+/g, '$1/')} target="_blank" rel="noopener noreferrer"><i className="pi pi-image" style={{ fontSize: '1.1rem', color: '#1570ef' }} /></a>
                                : <span style={{ color: '#d0d5dd' }}>—</span>} style={{ width: 60, textAlign: 'center' }} />
                            <Column field="estado"            header="Estado"      sortable body={estadoTemplate}      style={{ minWidth: 130 }} />
                            <Column header=""                 body={accionesTemplate}    style={{ width: 100, textAlign: 'center' }} />
                        </DataTable>
                    </>
                )
            }

            {/* ─── Modal: Seguimiento / trazabilidad del reclamo ───────────────── */}
            <Dialog header={reclamoActivo ? `Reclamo — ${reclamoActivo.tipoReclamoNombre}` : ''} visible={modalVisible} style={{ width: '560px' }} modal
                onHide={() => setModalVisible(false)}>
                {reclamoActivo && (
                    <div>
                        <div style={{ background: '#f8f9fa', border: '1px solid #e4e7ec', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
                            <p style={{ margin: '0 0 0.35rem' }}><strong>Solicitante:</strong> {reclamoActivo.solicitante}</p>
                            <p style={{ margin: '0 0 0.35rem' }}><strong>Fecha:</strong> {fmtFecha(reclamoActivo.fecha)}</p>
                            {reclamoActivo.descripcion && <p style={{ margin: '0 0 0.35rem' }}><strong>Descripción:</strong> {reclamoActivo.descripcion}</p>}
                            {reclamoActivo.fotoUrl && (
                                <a href={`${HOST_API}${reclamoActivo.fotoUrl}`.replace(/([^:])\/\/+/g, '$1/')} target="_blank" rel="noopener noreferrer">
                                    <img src={`${HOST_API}${reclamoActivo.fotoUrl}`.replace(/([^:])\/\/+/g, '$1/')} alt="Foto del reclamo"
                                        style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 6, marginTop: '0.35rem' }} />
                                </a>
                            )}
                        </div>

                        <div className="flex justify-content-end mb-3">
                            <Button
                                label="Generar orden de trabajo"
                                icon="pi pi-clipboard"
                                className="p-button-sm p-button-outlined"
                                onClick={() => navigate('/ordenesTrabajoAdmin', {
                                    state: {
                                        desdeReclamo: true,
                                        reclamoId: reclamoActivo.reclamoId,
                                        tipoReclamoNombre: reclamoActivo.tipoReclamoNombre,
                                        descripcion: reclamoActivo.descripcion,
                                    },
                                })}
                            />
                        </div>

                        <h6 style={{ margin: '0 0 0.5rem', color: '#344054' }}>Trazabilidad</h6>
                        <LoadingOverlay visible={loadingSeguimientos} />
                        {!loadingSeguimientos && (
                            <div style={{ maxHeight: 220, overflowY: 'auto', marginBottom: '1rem' }}>
                                {seguimientos.map((s) => (
                                    <div key={s.reclamoSeguimientoId} style={{ borderLeft: '3px solid #d0d5dd', paddingLeft: '0.75rem', marginBottom: '0.75rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                            {estadoBadge(s.estado)}
                                            <span style={{ fontSize: '0.78rem', color: '#98a2b3' }}>{fmtFechaHora(s.fecha)}{s.usuario ? ` · ${s.usuario}` : ''}</span>
                                        </div>
                                        {s.texto && <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', color: '#344054' }}>{s.texto}</p>}
                                    </div>
                                ))}
                            </div>
                        )}

                        <h6 style={{ margin: '0 0 0.5rem', color: '#344054' }}>Agregar actualización</h6>
                        <div className="grid p-fluid">
                            <div className="col-12">
                                <Dropdown value={nuevoEstado} options={ESTADOS.map(e => ({ label: e, value: e }))}
                                    onChange={(e) => setNuevoEstado(e.value)} placeholder="Seleccionar estado" className="p-inputtext-sm" />
                            </div>
                            <div className="col-12 mt-2">
                                <InputTextarea value={nuevoTexto} onChange={(e) => setNuevoTexto(e.target.value)}
                                    rows={3} placeholder="Comentario para el residente (opcional)" className="p-inputtext-sm" />
                            </div>
                            <div className="col-12 mt-2 flex justify-content-end">
                                <Button label="Agregar" icon="pi pi-plus" className="p-button-sm" onClick={guardarSeguimiento} loading={guardando} />
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>
        </>
    );
};

export default ReclamosAdmin;
